<template>
    <Modal
        size="md"
        :title="$t('targets.expectations')"
        :okTitle="$t('modal.save')"
        :okDisabled="false"
        :cancelDisabled="false"
        :cancelTitle="$t('modal.cancel')"
        :noCloseOnEsc="true"
        :noCloseOnBackdrop="true"
        isModalRoute
        @okHandler="onSubmit"
    >
        <c-loader :loader="isLoading" />
        <b-form id="form-checkin" novalidate v-show="!isLoading">
            <b-form-group
                :label="$t('targets.visualizationType.visualizationType')"
                :label-for="`visualizationType`"
                label-cols="12"
                label-cols-sm="6"
                class="ml-2 mr-3"
            >
                <Select
                    :selected="$v.form.visualizationType.$model"
                    :options="visualizationOptions"
                    @changeValue="onChangeValue"
                    :state="$v.form.visualizationType.$dirty ? !$v.form.visualizationType.$invalid : null"
                />
            </b-form-group>
            <b-container>
                <b-row :key="expectation.id.$model" v-for="expectation in $v.form.expectations.$each.$iter">
                    <b-col sm="5">
                        <b-form-group
                            :label="$t('form.label.value')"
                            :label-for="`input-value-id-${expectation.id.$model}`"
                            label-size="sm"
                            label-cols="2"
                            label-cols-sm="3"
                        >
                            <b-form-input
                                block
                                size="sm"
                                type="text"
                                :id="`input-value-id-${expectation.id.$model}`"
                                :aria-describedby="`input-value-id-${expectation.id.$model}-feedback`"
                                v-model="expectation.value.$model"
                                class="mr-2"
                                :state="checkArrayState(expectation, 'value')"
                            ></b-form-input>
                            <b-form-invalid-feedback :id="`input-value-id-${expectation.id.$model}-feedback`">
                                <span v-if="!expectation.value.decimal">
                                    {{ $t('form.validations.decimal') }}
                                </span>
                            </b-form-invalid-feedback>
                        </b-form-group>
                    </b-col>
                    <b-col sm="5">
                        <b-form-group
                            :label="$t('form.label.date')"
                            :label-for="`input-date-id-${expectation.id.$model}`"
                            label-size="sm"
                            label-cols="2"
                            label-cols-sm="3"
                        >
                            <v-date-picker
                                :min-date="form.period ? form.period.startDate : null"
                                :max-date="form.period ? form.period.endDate : null"
                                v-model="expectation.date.$model"
                                nav-visibility="click"
                                :popover="{ visibility: 'click' }"
                            >
                                <template v-slot="{ inputValue, inputEvents }">
                                    <b-form-input
                                        block
                                        size="sm"
                                        type="text"
                                        :id="`input-date-id-${expectation.id.$model}`"
                                        :aria-describedby="`input-date-id-${expectation.id.$model}-feedback`"
                                        :value="inputValue"
                                        v-on="inputEvents"
                                        @change.native="updateValue($event.target.value)"
                                        class="mr-2"
                                        :state="checkArrayState(expectation, 'date')"
                                        readonly
                                    ></b-form-input>
                                    <b-form-invalid-feedback :id="`input-date-id-${expectation.id.$model}-feedback`">
                                        <span v-if="!expectation.date.required">
                                            {{ $t('form.validations.required') }}
                                        </span>
                                    </b-form-invalid-feedback>
                                </template>
                            </v-date-picker>
                        </b-form-group>
                    </b-col>
                    <b-col sm="2">
                        <b-button
                            block
                            class="mb-3"
                            size="sm"
                            type="button"
                            variant="danger"
                            @click="
                                () => {
                                    form.expectations = onClickRemove(expectation.id.$model, form.expectations)
                                }
                            "
                        >
                            <i class="fa fa-remove"></i>
                        </b-button>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col sm="2" offset-sm="10">
                        <b-button
                            block
                            size="sm"
                            type="button"
                            variant="success"
                            @click="
                                () => {
                                    form.expectations = onClickAdd(form.expectations)
                                }
                            "
                        >
                            <i class="fa fa-plus"></i>
                        </b-button>
                    </b-col>
                </b-row>
            </b-container>
        </b-form>
        <template slot="okButton">
            <i v-if="isSubmitting" class="fa fa-spinner fa-spin px-2"></i>
        </template>
    </Modal>
</template>

<script>
import i18n from '@/i18n'
import Modal from '@/views/components/modal'
import { Select } from '@/views/components/selects'
import { validationMixin } from 'vuelidate'
import CLoader from '@/views/components/loader'
import { required, decimal } from 'vuelidate/lib/validators'
import { getExpectancies, updateExpectations, getPeriod } from './api'
import { cloneDeep } from 'lodash'
import { getFirstDayOfWeek, getDateFromStringWithoutTime } from '@/lib/date'
import { setupCalendar, DatePicker as VDatePicker } from 'v-calendar'
import notification, { success, danger } from '@/lib/notification'
import Swal from 'sweetalert2'

setupCalendar({
    locale: i18n.locale,
    navVisibility: 'focus',
    popoverDirection: 'bottom',
    popoverVisibility: 'focus',
    firstDayOfWeek: getFirstDayOfWeek(i18n.locale),
})

notification.init()

const visualizationOptions = [
    {
        label: i18n.t('targets.visualizationType.year_to_date'),
        value: 'year_to_date',
    },
    {
        label: i18n.t('targets.visualizationType.full_year_view'),
        value: 'full_year_view',
    },
    {
        label: i18n.t('targets.visualizationType.full_year_view_forecast'),
        value: 'full_year_view_forecast',
    },
]

const mapperCheckInsToForm = (checkIns) => {
    return checkIns.map((checkin) => {
        const { id, current_value, date } = checkin
        return {
            id,
            value: current_value,
            date: getDateFromStringWithoutTime(date, 'YYYY-MM-DD'),
        }
    })
}

const mapperCheckInsToApi = (checkIns) => {
    return checkIns.map((checkin) => {
        const { id, value, date } = checkin
        return {
            id,
            due_date: date,
            current_value: value,
        }
    })
}

const mapperPeriodToForm = (period) => {
    const { start_date, end_date, id } = period
    return {
        id,
        startDate: getDateFromStringWithoutTime(start_date, 'YYYY-MM-DD'),
        endDate: getDateFromStringWithoutTime(end_date, 'YYYY-MM-DD'),
    }
}

const getNewExpectancy = () => ({
    id: new Date().getTime(),
    value: null,
    date: new Date(),
})

const initialValuesOfFields = {
    period: null,
    expectations: null,
    visualizationType: null,
}

export default {
    name: 'Expectancy',
    components: {
        Modal,
        CLoader,
        VDatePicker,
        Select,
    },
    mixins: [validationMixin],
    validations: {
        form: {
            period: {
                required,
            },
            expectations: {
                required,
                $each: {
                    id: {},
                    value: {
                        decimal,
                    },
                    date: {
                        required,
                    },
                },
            },
            visualizationType: {},
        },
    },
    data() {
        return {
            isLoading: true,
            isSubmitting: false,
            form: { ...initialValuesOfFields },
            indicatorID: null,
            indicatorType: null,
            visualizationOptions: [],
        }
    },
    mounted() {
        this.visualizationOptions = visualizationOptions
        if (this.$route.params.targetId) {
            this.indicatorID = this.$route.params.targetId
            this.indicatorType = 'target'
        } else if (this.$route.params.kpiId) {
            this.indicatorID = this.$route.params.kpiId
            this.indicatorType = 'kpi'
        }
        getExpectancies(this.indicatorID, this.indicatorType).then((resp) => {
            const {
                data: { data },
            } = resp
            this.form.expectations = mapperCheckInsToForm(data.checkins)
            this.form.visualizationType = this.getVisualizationOption(data.visualization_type)
            getPeriod(data.period_id)
                .then((resp) => {
                    const {
                        data: { data },
                    } = resp
                    this.form.period = mapperPeriodToForm(data)
                })
                .finally(() => {
                    this.isLoading = false
                })
        })
    },
    destroyed() {
        this.$emit('update-parent', 'updateCheckin', null)
    },
    methods: {
        getVisualizationOption(value) {
            return this.visualizationOptions.find((visualization) => visualization.value == value)
        },
        onChangeValue(visualization) {
            this.$v.form.visualizationType.$model = this.getVisualizationOption(visualization.value)
        },
        onSubmit(event, closeModal) {
            event.preventDefault()
            if (this.validate()) {
                this.$nextTick(() => {
                    this.isLoading = true
                    updateExpectations(
                        this.indicatorID,
                        this.form.period.id,
                        mapperCheckInsToApi(this.form.expectations),
                        this.$v.form.visualizationType.$model.value,
                        this.indicatorType
                    )
                        .then((resp) => {
                            if (resp.data.messages[0].status === 'success') {
                                this.notificationRangeEditedSuccessfully()
                                closeModal()
                            } else {
                                if (resp.data.messages[0].params === 'redundantExpectations') {
                                    Swal({
                                        type: 'error',
                                        title: i18n.t('notification.somethingWentWrong'),
                                        text: i18n.t('notification.checkin.updateExpectations'),
                                    })
                                }
                            }
                        })
                        .catch(() => {
                            this.notificationError()
                        })
                        .finally(() => {
                            this.isLoading = false
                        })
                })
            }
        },
        checkArrayState(current, key) {
            return current[key].$dirty ? !current[key].$invalid : null
        },
        validate() {
            this.$v.$touch()
            return !this.$v.$invalid
        },
        onClickRemove(checkInID, expectations) {
            return expectations.filter((expectation) => expectation.id != checkInID)
        },
        onClickAdd(expectations) {
            return [...cloneDeep(expectations), getNewExpectancy()]
        },
        notificationRangeEditedSuccessfully() {
            success(
                i18n.t('notification.updatedSuccessfully', {
                    item: i18n.t('targets.expectations'),
                })
            )
        },
        notificationError() {
            danger()
        },
    },
}
</script>

<style>
#form-checkin .form-control[readonly] {
    background-color: initial;
}
</style>
